import { useState, useRef } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Save, Plus, CameraIcon, Sparkles, BarChart3, InfoIcon, Check, Eraser, ScanSearch, AlertTriangle, X, Loader2 } from 'lucide-react';
import { Condition, CONDITION_LABELS, Platform, PLATFORM_NAMES } from '@crosova/shared';
import { api } from '../../lib/api';

const STEPS = [
  { num: 1, label: 'Photos', sub: 'Upload images' },
  { num: 2, label: 'Details', sub: 'Title, price, condition' },
  { num: 3, label: 'Platforms', sub: 'Where to list' },
  { num: 4, label: 'Per-platform', sub: 'Category & fields' },
  { num: 5, label: 'Review & publish', sub: 'Go live' },
];

const PLATFORM_COLOURS: Record<string, string> = {
  ebay: '#e53935', vinted: '#09b864', depop: '#ff2300', etsy: '#f56040', facebook: '#1877f2',
};

interface FormState {
  photos: { id: string; url: string; s3Key: string }[];
  title: string;
  description: string;
  category: string;
  subcategory: string;
  size: string;
  condition: Condition | '';
  price: string;
  buyPrice: string;
  brand: string;
  colour: string;
  sku: string;
  tags: string[];
  selectedPlatforms: Set<Platform>;
  scheduledAt: string;
  scheduleEnabled: boolean;
  perPlatformOverrides: Record<string, { title?: string; price?: string }>;
}

export function NewListingWizard() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<FormState>({
    photos: [],
    title: '',
    description: '',
    category: '',
    subcategory: '',
    size: '',
    condition: '',
    price: '',
    buyPrice: '',
    brand: '',
    colour: '',
    sku: '',
    tags: [],
    selectedPlatforms: new Set(),
    scheduledAt: '',
    scheduleEnabled: false,
    perPlatformOverrides: {},
  });
  const navigate = useNavigate();
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const clearError = (field: string) =>
    setErrors((e) => {
      if (!e[field]) return e;
      const next = { ...e };
      delete next[field];
      return next;
    });

  // Required fields per step. Blocks Continue until they're valid.
  function validateStep(n: number): Record<string, string> {
    const e: Record<string, string> = {};
    if (n === 1 && form.photos.length === 0) e.photos = 'Add at least one photo to continue.';
    if (n === 2) {
      if (!form.title.trim()) e.title = 'Title is required.';
      const price = parseFloat(form.price);
      if (!form.price || isNaN(price) || price <= 0) e.price = 'Enter a price greater than £0.';
      if (!form.condition) e.condition = 'Select a condition.';
    }
    if (n === 3 && form.selectedPlatforms.size === 0) e.platforms = 'Select at least one platform.';
    return e;
  }

  function goNext() {
    const e = validateStep(step);
    setErrors(e);
    if (Object.keys(e).length === 0) setStep(step + 1);
  }

  // Photos are uploaded to storage during the wizard but have no DB record yet
  // (no listing existed). Once the listing is created, attach each one in order.
  async function attachPhotos(listingId: string) {
    for (const photo of form.photos) {
      await api.post(`/listings/${listingId}/photos/attach`, { url: photo.url, s3Key: photo.s3Key });
    }
  }

  async function handleSaveDraft() {
    if (!form.title.trim()) return;
    const draft = await api.post('/listings', {
      title: form.title,
      description: form.description || '',
      price: form.price ? Math.round(parseFloat(form.price) * 100) : 100,
      condition: form.condition || 'good',
      brand: form.brand,
      colour: form.colour,
      size: form.size,
      category: form.category,
      sku: form.sku,
      tags: form.tags,
      buyPricePence: form.buyPrice ? Math.round(parseFloat(form.buyPrice) * 100) : undefined,
    });
    await attachPhotos(draft.data.id);
    navigate('/dashboard/inventory');
  }

  async function handlePublish() {
    setPublishError(null);

    // Friendly pre-checks so the user gets a clear message instead of a raw
    // "Validation failed" from the API (or a silent nothing).
    const priceNum = parseFloat(form.price);
    if (!form.price || isNaN(priceNum) || priceNum <= 0) {
      setPublishError('Enter a price greater than £0 on the Details step.');
      return;
    }
    if (!form.condition) {
      setPublishError('Choose a condition (e.g. Good used) on the Details step.');
      return;
    }
    if (!form.title.trim()) {
      setPublishError('Add a title on the Details step.');
      return;
    }
    if (!form.scheduleEnabled && form.photos.length === 0) {
      setPublishError('Add at least one photo before publishing. Go back to the Photos step to add one.');
      return;
    }
    if (!form.scheduleEnabled && form.selectedPlatforms.size === 0) {
      setPublishError('Select at least one platform to publish to (Platforms step).');
      return;
    }

    setPublishing(true);
    try {
      await doPublish();
    } catch (err: any) {
      const data = err?.response?.data ?? {};
      let msg = data.error ?? data.message ?? 'Something went wrong while publishing. Please try again.';
      // Zod validation errors come back as { error: 'Validation failed', details: [{ path, message }] }
      const details = data.details as { path?: (string | number)[]; message?: string }[] | undefined;
      if (details?.length) {
        const fields = details.map((d) => (d.path?.filter((p) => typeof p === 'string').join('.') || 'field')).join(', ');
        msg = `Please check these fields: ${fields}.`;
      } else if (Array.isArray(data.missing) && data.missing.length) {
        msg = `${msg}: ${data.missing.join(', ')}`;
      }
      setPublishError(msg);
      setPublishing(false);
    }
  }

  async function doPublish() {
    const payload: Record<string, any> = {
      title: form.title,
      description: form.description,
      price: Math.round(parseFloat(form.price) * 100),
      condition: form.condition,
      brand: form.brand,
      colour: form.colour,
      size: form.size,
      category: form.category,
      subcategory: form.subcategory,
      sku: form.sku,
      tags: form.tags,
    };
    if (form.buyPrice) payload.buyPricePence = Math.round(parseFloat(form.buyPrice) * 100);

    if (form.scheduleEnabled && form.scheduledAt) {
      payload.scheduledPublishAt = new Date(form.scheduledAt).toISOString();
      payload.scheduledPlatforms = Array.from(form.selectedPlatforms);
      const scheduled = await api.post('/listings', payload);
      await attachPhotos(scheduled.data.id);
      navigate('/dashboard/inventory');
      return;
    }

    const listing = await api.post('/listings', payload);
    await attachPhotos(listing.data.id);

    // Build per-platform overrides for crosslist (only include non-empty values)
    const overrides: Record<string, any> = {};
    for (const [platform, vals] of Object.entries(form.perPlatformOverrides)) {
      const o: Record<string, any> = {};
      if (vals.title?.trim()) o.title = vals.title.trim();
      if (vals.price && parseInt(vals.price) > 0) o.price = parseInt(vals.price);
      if (Object.keys(o).length > 0) overrides[platform] = o;
    }

    await api.post(`/listings/${listing.data.id}/crosslist`, {
      platforms: Array.from(form.selectedPlatforms),
      perPlatformOverrides: Object.keys(overrides).length > 0 ? overrides : undefined,
    });
    navigate('/dashboard/jobs');
  }

  return (
    <div className="flex flex-col h-full -m-5">
      {/* Top bar */}
      <header className="h-[52px] bg-bg-surface border-b border-white/[0.06] flex items-center justify-between px-5">
        <div className="flex items-center gap-2.5">
          <div className="w-[30px] h-[30px] bg-primary-500 rounded-lg flex items-center justify-center font-bold text-xs text-white">Cr</div>
          <div className="text-[15px] font-semibold tracking-tight">Cros<span className="text-secondary-400">ova</span></div>
        </div>
        <div className="flex items-center gap-2.5">
          <button onClick={() => navigate('/dashboard/inventory')} className="btn-secondary flex items-center gap-1.5 !py-1.5">
            <ArrowLeft size={13} /> Back to inventory
          </button>
          <button
            onClick={handleSaveDraft}
            disabled={!form.title.trim()}
            className="bg-secondary-400/[0.06] border border-secondary-400/20 text-secondary-400 rounded-md px-3 py-1.5 text-xs flex items-center gap-1.5 disabled:opacity-40"
          >
            <Save size={12} /> Save draft
          </button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Steps panel */}
        <aside className="w-[200px] bg-bg-surface border-r border-white/[0.05] p-6">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-white/20 mb-5">New listing</div>
          {STEPS.map((s, idx) => (
            <div key={s.num}>
              <div className={`flex gap-2.5 p-2 rounded-lg cursor-pointer ${
                step === s.num ? 'bg-primary-500/[0.1]' : ''
              }`} onClick={() => s.num <= step && setStep(s.num)}>
                <div className={`w-[22px] h-[22px] rounded-full flex items-center justify-center text-[10px] font-semibold border-[1.5px] flex-shrink-0 mt-px ${
                  s.num < step
                    ? 'bg-secondary-400 border-secondary-400 text-bg-base'
                    : s.num === step
                    ? 'bg-primary-500 border-primary-500 text-white'
                    : 'border-white/[0.12] text-white/25'
                }`}>
                  {s.num < step ? <Check size={11} strokeWidth={3} /> : s.num}
                </div>
                <div>
                  <div className={`text-xs font-medium ${
                    s.num === step ? 'text-white' : s.num < step ? 'text-white/40' : 'text-white/25'
                  }`}>{s.label}</div>
                  <div className={`text-[10px] mt-0.5 ${s.num === step ? 'text-white/35' : 'text-white/[0.18]'}`}>{s.sub}</div>
                </div>
              </div>
              {idx < STEPS.length - 1 && (
                <div className={`w-px h-3.5 ml-[18px] my-1 ${s.num < step ? 'bg-secondary-400/30' : 'bg-white/[0.06]'}`} />
              )}
            </div>
          ))}
        </aside>

        {/* Form area */}
        <div className="flex-1 p-6 overflow-y-auto flex flex-col gap-4.5">
          {step === 1 && <PhotoStep form={form} update={update} errors={errors} clearError={clearError} />}
          {step === 2 && <DetailsStep form={form} update={update} errors={errors} clearError={clearError} />}
          {step === 3 && <PlatformStep form={form} update={update} errors={errors} clearError={clearError} />}
          {step === 4 && <PerPlatformStep form={form} update={update} />}
          {step === 5 && <ReviewStep form={form} update={update} />}

          {publishError && (
            <div className="flex items-start gap-2 rounded-lg bg-red-500/10 border border-red-500/25 px-3 py-2.5 text-[12px] text-red-300">
              <AlertTriangle size={14} className="flex-shrink-0 mt-px" />
              <span>{publishError}</span>
            </div>
          )}

          <div className="flex items-center justify-between pt-2 border-t border-white/[0.05] mt-auto">
            <div className="flex gap-1.5">
              {STEPS.map((s) => (
                <div
                  key={s.num}
                  className={`h-1.5 rounded transition-all ${
                    s.num === step ? 'w-4 bg-primary-500' : s.num < step ? 'w-1.5 bg-secondary-400' : 'w-1.5 bg-white/10'
                  }`}
                />
              ))}
            </div>
            <div className="flex gap-2">
              {step > 1 && <button onClick={() => setStep(step - 1)} className="btn-secondary">Back</button>}
              {step < 5 ? (
                <button onClick={goNext} className="btn-primary flex items-center gap-1.5">
                  Continue <ArrowRight size={13} />
                </button>
              ) : (
                <button onClick={handlePublish} disabled={publishing} className="btn-primary flex items-center gap-1.5 disabled:opacity-60">
                  {publishing ? <><Loader2 size={13} className="animate-spin" /> Publishing…</> : <>Publish <ArrowRight size={13} /></>}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface PhotoAISuggestion {
  title: string; brand: string; category: string; colour: string;
  size: string; condition: string; suggestedPricePence: number;
  tags: string[]; description: string;
}

function PhotoStep({ form, update, errors, clearError }: { form: FormState; update: any; errors: Record<string, string>; clearError: (f: string) => void }) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [removingBgId, setRemovingBgId] = useState<string | null>(null);
  const [aiSuggestion, setAiSuggestion] = useState<PhotoAISuggestion | null>(null);
  const [scanning, setScanning] = useState(false);

  async function handleRemoveBg(photoId: string) {
    setRemovingBgId(photoId);
    try {
      const res = await api.post<{ id: string; url: string }>('/uploads/remove-bg', { photoId });
      update('photos', form.photos.map((p: { id: string; url: string }) =>
        p.id === photoId ? { ...p, url: res.data.url } : p,
      ));
    } catch { /* silently fail */ } finally {
      setRemovingBgId(null);
    }
  }

  async function scanPhoto(url: string) {
    setScanning(true);
    try {
      const { data } = await api.post<PhotoAISuggestion>('/ai/identify-from-photo', { imageUrl: url });
      setAiSuggestion(data);
    } catch { /* silently fail if AI not configured */ } finally {
      setScanning(false);
    }
  }

  function applyAiSuggestion() {
    if (!aiSuggestion) return;
    if (aiSuggestion.title) update('title', aiSuggestion.title);
    if (aiSuggestion.brand) update('brand', aiSuggestion.brand);
    if (aiSuggestion.category) update('category', aiSuggestion.category);
    if (aiSuggestion.colour) update('colour', aiSuggestion.colour);
    if (aiSuggestion.size) update('size', aiSuggestion.size);
    if (aiSuggestion.condition) update('condition', aiSuggestion.condition as Condition);
    if (aiSuggestion.suggestedPricePence) update('price', (aiSuggestion.suggestedPricePence / 100).toFixed(2));
    if (aiSuggestion.tags?.length) update('tags', aiSuggestion.tags);
    if (aiSuggestion.description) update('description', aiSuggestion.description);
    setAiSuggestion(null);
  }

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    const remaining = 25 - form.photos.length;
    if (remaining <= 0) return;
    setUploading(true);
    setUploadError('');
    try {
      const toUpload = Array.from(files).slice(0, remaining);
      const uploaded = await Promise.all(
        toUpload.map(async (file) => {
          const fd = new FormData();
          fd.append('file', file);
          // /uploads/photo stores the file and returns { url, s3Key } — there is
          // no DB record yet (no listing exists). We give it a temporary client
          // id for the UI, and attach it to the listing at publish time.
          const res = await api.post<{ url: string; s3Key: string }>('/uploads/photo', fd);
          return { id: crypto.randomUUID(), url: res.data.url, s3Key: res.data.s3Key };
        }),
      );
      const newPhotos = [...form.photos, ...uploaded];
      update('photos', newPhotos);
      clearError('photos');
      // Auto-scan the cover photo if this is the first upload
      if (form.photos.length === 0 && uploaded[0]) {
        scanPhoto(uploaded[0].url);
      }
    } catch {
      setUploadError('Upload failed — check your internet connection and try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  function removePhoto(id: string) {
    update('photos', form.photos.filter((p: { id: string }) => p.id !== id));
  }

  return (
    <>
      <header>
        <h2 className="text-[17px] font-semibold tracking-tight">Photos <span className="text-red-400">*</span></h2>
        <p className="text-xs text-white/30 mt-0.5">Add up to 25 photos. The first one is the cover.</p>
      </header>

      {errors.photos && (
        <div className="flex items-center gap-2 rounded-lg bg-red-500/10 border border-red-500/30 px-3 py-2 text-[12px] text-red-300">
          <AlertTriangle size={14} className="flex-shrink-0" /> {errors.photos}
        </div>
      )}

      {/* AI scan result banner */}
      {scanning && (
        <div className="flex items-center gap-2.5 bg-primary-500/[0.08] border border-primary-500/20 rounded-xl px-4 py-3">
          <div className="w-4 h-4 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin flex-shrink-0" />
          <span className="text-[12.5px] text-primary-300">AI is scanning your photo...</span>
        </div>
      )}

      {aiSuggestion && !scanning && (
        <div className="bg-secondary-400/[0.07] border border-secondary-400/25 rounded-xl p-4">
          <div className="flex items-start justify-between mb-2.5">
            <div className="flex items-center gap-2">
              <ScanSearch size={14} className="text-secondary-400" />
              <span className="text-[12.5px] font-semibold text-secondary-400">AI identified your item</span>
            </div>
            <button onClick={() => setAiSuggestion(null)} className="text-white/25 hover:text-white/50">
              <X size={14} />
            </button>
          </div>
          <div className="text-[12px] text-white/70 mb-1 font-medium">{aiSuggestion.title}</div>
          <div className="text-[11px] text-white/40">
            {[aiSuggestion.brand, aiSuggestion.category, aiSuggestion.colour, aiSuggestion.size]
              .filter(Boolean).join(' · ')}
            {aiSuggestion.suggestedPricePence > 0 && ` · Suggested £${(aiSuggestion.suggestedPricePence / 100).toFixed(2)}`}
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={applyAiSuggestion} className="btn-primary text-[12px] flex items-center gap-1.5">
              <Sparkles size={12} /> Apply to listing
            </button>
            <button onClick={() => setAiSuggestion(null)} className="btn-secondary text-[12px]">
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Rescan button (shown after first photo if no suggestion active) */}
      {form.photos.length > 0 && !scanning && !aiSuggestion && (
        <button
          onClick={() => scanPhoto(form.photos[0].url)}
          className="flex items-center gap-1.5 text-[11.5px] text-white/30 hover:text-white/60 transition-colors self-start"
        >
          <ScanSearch size={13} /> Scan cover photo with AI
        </button>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />

      <div className="flex gap-2 flex-wrap items-start">
        {/* Cover slot */}
        {form.photos.length === 0 ? (
          <button
            onClick={() => fileInputRef.current?.click()}
            className="w-[120px] h-[120px] border-[1.5px] border-dashed border-primary-500/35 rounded-xl bg-primary-500/[0.05] flex flex-col items-center justify-center cursor-pointer gap-1.5 hover:border-primary-500/60 transition-colors"
          >
            <CameraIcon size={22} className="text-primary-500/60" />
            <div className="text-[10px] text-primary-500/60">Add cover photo</div>
          </button>
        ) : (
          <div className="relative w-[120px] h-[120px] group">
            <img
              src={form.photos[0].url}
              alt="Cover"
              className="w-full h-full object-cover rounded-xl border border-white/10"
            />
            <button
              onClick={() => removePhoto(form.photos[0].id)}
              className="absolute top-1 right-1 w-5 h-5 bg-black/60 rounded-full text-white/70 text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            >
              ×
            </button>
            <button
              onClick={() => handleRemoveBg(form.photos[0].id)}
              disabled={removingBgId === form.photos[0].id}
              title="Remove background"
              className="absolute bottom-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 rounded px-1.5 py-0.5 flex items-center gap-1 disabled:opacity-50"
            >
              {removingBgId === form.photos[0].id
                ? <div className="w-3 h-3 border border-white/40 border-t-white rounded-full animate-spin" />
                : <Eraser size={10} className="text-secondary-400" />}
              <span className="text-[9px] text-secondary-400">Remove bg</span>
            </button>
            <div className="absolute bottom-1 left-1 bg-black/50 text-[9px] text-white/60 px-1.5 rounded">Cover</div>
          </div>
        )}

        {/* Additional photos */}
        {form.photos.slice(1).map((photo: { id: string; url: string }) => (
          <div key={photo.id} className="relative w-14 h-14 group">
            <img src={photo.url} alt="" className="w-full h-full object-cover rounded-lg border border-white/10" />
            <button
              onClick={() => removePhoto(photo.id)}
              className="absolute top-0.5 right-0.5 w-4 h-4 bg-black/60 rounded-full text-white/70 text-[10px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            >
              ×
            </button>
          </div>
        ))}

        {/* Add more / loading */}
        {form.photos.length < 25 && (
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="w-14 h-14 rounded-lg border border-dashed border-white/10 bg-white/[0.03] flex items-center justify-center cursor-pointer hover:border-white/20 transition-colors disabled:opacity-50"
          >
            {uploading ? (
              <div className="w-4 h-4 border-2 border-primary-500/40 border-t-primary-500 rounded-full animate-spin" />
            ) : (
              <Plus size={16} className="text-white/[0.12]" />
            )}
          </button>
        )}
      </div>

      {uploadError && <p className="text-xs text-orange-400 mt-1">{uploadError}</p>}
      {form.photos.length > 0 && (
        <p className="text-[10px] text-white/25">{form.photos.length} / 25 photos added</p>
      )}
    </>
  );
}

function DetailsStep({ form, update, errors, clearError }: { form: FormState; update: any; errors: Record<string, string>; clearError: (f: string) => void }) {
  const [showTemplates, setShowTemplates] = useState(false);

  const { data: policies = [] } = useQuery({
    queryKey: ['policies'],
    queryFn: () => api.get('/policies').then((r) => r.data),
  });

  // Duplicate check — runs when title is long enough
  const duplicate = useQuery<{ isDuplicate: boolean; duplicateId?: string }>({
    queryKey: ['check-duplicate', form.title],
    queryFn: () =>
      api.post('/listings/check-duplicate', { title: form.title, sku: form.sku || undefined })
        .then((r) => r.data),
    enabled: form.title.trim().length >= 10,
    staleTime: 30_000,
  });
  const { data: templates = [] } = useQuery<{ id: string; name: string; description?: string; category?: string; subcategory?: string; brand?: string; colour?: string; size?: string; condition?: string; price?: number; tags: string[] }[]>({
    queryKey: ['templates'],
    queryFn: () => import('../../lib/api').then(({ api }) => api.get('/templates').then((r) => r.data)),
  });

  function applyTemplate(t: typeof templates[number]) {
    if (t.description) update('description', t.description);
    if (t.category) update('category', t.category);
    if (t.subcategory) update('subcategory', t.subcategory);
    if (t.brand) update('brand', t.brand);
    if (t.colour) update('colour', t.colour);
    if (t.size) update('size', t.size);
    if (t.condition) update('condition', t.condition as Condition);
    if (t.price) update('price', (t.price / 100).toFixed(2));
    if (t.tags?.length) update('tags' as any, t.tags);
    setShowTemplates(false);
  }

  return (
    <>
      <header className="flex items-start justify-between">
        <div>
          <h2 className="text-[17px] font-semibold">Item details</h2>
          <p className="text-xs text-white/30 mt-0.5">Crosova maps these to each platform automatically</p>
        </div>
        {templates.length > 0 && (
          <div className="relative">
            <button
              onClick={() => setShowTemplates(!showTemplates)}
              className="btn-secondary !py-1 !px-2.5 text-[11px] flex items-center gap-1"
            >
              Load template
            </button>
            {showTemplates && (
              <div className="absolute right-0 top-8 bg-bg-raised border border-white/[0.1] rounded-xl w-56 shadow-xl z-10 overflow-hidden">
                {templates.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => applyTemplate(t)}
                    className="w-full text-left px-3 py-2.5 text-[11.5px] hover:bg-white/[0.04] border-b border-white/[0.04] last:border-0"
                  >
                    <div className="font-medium text-white/80">{t.name}</div>
                    {t.brand && <div className="text-[10px] text-white/30 mt-0.5">{t.brand}{t.size ? ` · ${t.size}` : ''}</div>}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </header>

      {/* Duplicate warning */}
      {duplicate.data?.isDuplicate && (
        <div className="flex items-start gap-2.5 bg-amber-500/[0.08] border border-amber-500/20 rounded-xl px-4 py-3">
          <AlertTriangle size={14} className="text-amber-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-[12.5px] text-amber-300 font-medium">Possible duplicate</p>
            <p className="text-[11.5px] text-white/50 mt-0.5">
              A very similar listing already exists in your inventory.{' '}
              {duplicate.data.duplicateId && (
                <a href={`/dashboard/inventory`} className="text-primary-300 hover:underline">
                  View existing listing
                </a>
              )}
            </p>
          </div>
        </div>
      )}

      {/* Shipping policy selector */}
      {policies.length > 0 && (
        <div>
          <div className="text-[10px] text-white/35 mb-1">Shipping policy</div>
          <select
            className="input"
            defaultValue=""
            onChange={(e) => {
              const policy = policies.find((p: any) => p.id === e.target.value);
              if (policy) {
                // Policy is stored by reference — platform adapters will use it at publish time
                update('shippingPolicyId' as any, policy.id);
              }
            }}
          >
            <option value="">No policy selected</option>
            {policies.map((p: any) => (
              <option key={p.id} value={p.id}>
                {p.isDefault ? '★ ' : ''}{p.name}
                {p.freeShipping ? ' — Free shipping' : ` — £${(p.shippingCostPence / 100).toFixed(2)}`}
              </option>
            ))}
          </select>
        </div>
      )}

      <DetailsFields form={form} update={update} errors={errors} clearError={clearError} />
    </>
  );
}

function DetailsFields({ form, update, errors, clearError }: { form: FormState; update: any; errors: Record<string, string>; clearError: (f: string) => void }) {
  const [tagInput, setTagInput] = useState('');

  const genTitle = useMutation({
    mutationFn: () => api.post<{ title: string }>('/ai/generate-title', {
      brand: form.brand, category: form.category, size: form.size,
      colour: form.colour, condition: form.condition || 'good',
    }).then((r) => r.data),
    onSuccess: (d) => d.title && update('title', d.title),
  });

  const genDescription = useMutation({
    mutationFn: () => api.post<{ description: string }>('/ai/generate-description', {
      title: form.title, brand: form.brand, size: form.size,
      condition: form.condition || 'good',
    }).then((r) => r.data),
    onSuccess: (d) => d.description && update('description', d.description),
  });

  const genTags = useMutation({
    mutationFn: () => api.post<{ tags: string[] }>('/ai/generate-tags', {
      title: form.title, brand: form.brand, category: form.category,
    }).then((r) => r.data),
    onSuccess: (d) => d.tags?.length && update('tags', d.tags),
  });

  function addTag() {
    const t = tagInput.trim().replace(/^#/, '');
    if (t && !form.tags.includes(t)) update('tags', [...form.tags, t]);
    setTagInput('');
  }

  return (
    <>
      <Field label="Title" required error={errors.title} onAiAction={() => genTitle.mutate()} aiLoading={genTitle.isPending}>
        <input className="input" placeholder="e.g. Nike Air Max 90 Trainers White Black UK10" value={form.title} onChange={(e) => { update('title', e.target.value); clearError('title'); }} />
      </Field>

      <Field label="Description" onAiAction={() => genDescription.mutate()} aiLoading={genDescription.isPending}>
        <textarea className="input resize-none h-20" placeholder="Describe the item — condition, measurements, defects..." value={form.description} onChange={(e) => update('description', e.target.value)} />
      </Field>

      <div className="flex gap-3">
        <Field label="Category">
          <input className="input" placeholder="e.g. Clothing" value={form.category} onChange={(e) => update('category', e.target.value)} />
        </Field>
        <Field label="Sub-category">
          <input className="input" placeholder="e.g. Trainers" value={form.subcategory} onChange={(e) => update('subcategory', e.target.value)} />
        </Field>
        <Field label="Size">
          <input className="input" placeholder="UK 10" value={form.size} onChange={(e) => update('size', e.target.value)} />
        </Field>
      </div>

      <Field label="Condition" required error={errors.condition}>
        <div className="flex gap-1.5">
          {Object.values(Condition).map((c) => (
            <button
              key={c}
              onClick={() => { update('condition', c); clearError('condition'); }}
              className={`flex-1 px-1.5 py-1.5 rounded-md text-[11px] border transition-colors ${
                form.condition === c
                  ? 'border-primary-500/40 bg-primary-500/10 text-primary-300'
                  : 'border-white/[0.07] bg-white/[0.02] text-white/30'
              }`}
            >
              {CONDITION_LABELS[c]}
            </button>
          ))}
        </div>
      </Field>

      <Field label="Price" required priceAdvisor error={errors.price}>
        <div className="flex gap-2.5">
          <div className="flex-1 relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[13px] font-medium text-secondary-400">£</span>
            <input className="input pl-6" placeholder="0.00" value={form.price} onChange={(e) => { update('price', e.target.value); clearError('price'); }} />
          </div>
          <div className="bg-secondary-400/[0.05] border border-secondary-400/[0.1] rounded-lg px-3 py-2 text-[11px] text-secondary-400/60 flex items-center gap-1.5 whitespace-nowrap">
            <InfoIcon size={13} /> Similar sold for £75–£105
          </div>
        </div>
      </Field>

      <div className="flex gap-3">
        <Field label="Brand"><input className="input" value={form.brand} onChange={(e) => update('brand', e.target.value)} /></Field>
        <Field label="Colour"><input className="input" value={form.colour} onChange={(e) => update('colour', e.target.value)} /></Field>
        <Field label="SKU / label"><input className="input" value={form.sku} onChange={(e) => update('sku', e.target.value)} /></Field>
      </div>

      <Field label="Buy price — what you paid (optional)">
        <div className="flex items-center gap-2">
          <div className="relative w-36">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[13px] font-medium text-white/30">£</span>
            <input className="input pl-6" placeholder="0.00" type="number" min="0" step="0.01"
              value={form.buyPrice} onChange={(e) => update('buyPrice', e.target.value)} />
          </div>
          {form.price && form.buyPrice && (
            <span className={`text-[12px] font-medium ${parseFloat(form.price) > parseFloat(form.buyPrice) ? 'text-secondary-400' : 'text-danger'}`}>
              {parseFloat(form.price) > parseFloat(form.buyPrice)
                ? `+£${(parseFloat(form.price) - parseFloat(form.buyPrice)).toFixed(2)} profit`
                : `-£${(parseFloat(form.buyPrice) - parseFloat(form.price)).toFixed(2)} loss`}
            </span>
          )}
        </div>
        <p className="text-[10px] text-white/20 mt-1">Used to calculate your profit in analytics</p>
      </Field>

      <Field label="Tags" onAiAction={() => genTags.mutate()} aiLoading={genTags.isPending}>
        <div>
          {form.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-1.5">
              {form.tags.map((tag) => (
                <span key={tag} className="flex items-center gap-1 bg-primary-500/[0.08] border border-primary-500/20 text-primary-300 text-[10px] px-1.5 py-0.5 rounded">
                  #{tag}
                  <button onClick={() => update('tags', form.tags.filter((t: string) => t !== tag))} className="text-white/30 hover:text-white/70 leading-none">×</button>
                </span>
              ))}
            </div>
          )}
          <div className="flex gap-1.5">
            <input
              className="input flex-1 text-[11px]"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
              placeholder="Type a tag and press Enter"
            />
            <button onClick={addTag} className="btn-secondary !py-1 !px-2.5 text-[11px]">Add</button>
          </div>
        </div>
      </Field>
    </>
  );
}

function PlatformStep({ form, update, errors, clearError }: { form: FormState; update: any; errors: Record<string, string>; clearError: (f: string) => void }) {
  function toggle(p: Platform) {
    const next = new Set(form.selectedPlatforms);
    next.has(p) ? next.delete(p) : next.add(p);
    update('selectedPlatforms', next);
    clearError('platforms');
  }
  return (
    <>
      <header><h2 className="text-[17px] font-semibold">Where to list <span className="text-red-400">*</span></h2><p className="text-xs text-white/30 mt-0.5">Pick the platforms you want this item published on</p></header>
      {errors.platforms && (
        <div className="flex items-center gap-2 rounded-lg bg-red-500/10 border border-red-500/30 px-3 py-2 text-[12px] text-red-300">
          <AlertTriangle size={14} className="flex-shrink-0" /> {errors.platforms}
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {Object.values(Platform).slice(0, 5).map((p) => {
          const on = form.selectedPlatforms.has(p);
          return (
            <button
              key={p}
              onClick={() => toggle(p)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
                on ? 'border-secondary-400/30 bg-secondary-400/[0.06] text-secondary-400' : 'border-white/[0.07] bg-white/[0.02] text-white/35'
              }`}
            >
              <div className="w-2 h-2 rounded-full" style={{ background: PLATFORM_COLOURS[p] }} />
              <span className="text-xs">{PLATFORM_NAMES[p]}</span>
              <div className={`w-3.5 h-3.5 rounded ml-auto ${on ? 'bg-secondary-400' : 'border border-white/[0.12]'}`}>
                {on && <Check size={11} className="text-bg-base m-px" strokeWidth={3} />}
              </div>
            </button>
          );
        })}
      </div>
    </>
  );
}

function PerPlatformStep({ form, update }: { form: FormState; update: any }) {
  const overrides = (form as any).perPlatformOverrides ?? {};

  function setOverride(platform: string, field: string, value: string) {
    update('perPlatformOverrides' as any, {
      ...overrides,
      [platform]: { ...overrides[platform], [field]: value },
    });
  }

  if (form.selectedPlatforms.size === 0) {
    return (
      <>
        <header><h2 className="text-[17px] font-semibold">Per-platform options</h2></header>
        <p className="text-sm text-white/40">No platforms selected. Go back and choose at least one.</p>
      </>
    );
  }

  return (
    <>
      <header>
        <h2 className="text-[17px] font-semibold">Per-platform options</h2>
        <p className="text-xs text-white/30 mt-0.5">Override title or price for a specific platform — or leave blank to use your main listing details</p>
      </header>
      {Array.from(form.selectedPlatforms).map((p) => (
        <div key={p} className="card p-4 space-y-3">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: PLATFORM_COLOURS[p] }} />
            <span className="text-[13px] font-medium">{PLATFORM_NAMES[p]}</span>
          </div>
          <Field label={`Title override (optional — default: "${form.title || 'your listing title'}")`}>
            <input
              className="input text-[12px]"
              placeholder="Leave blank to use your main title"
              value={overrides[p]?.title ?? ''}
              onChange={(e) => setOverride(p, 'title', e.target.value)}
            />
          </Field>
          <Field label={`Price override (optional — default: £${form.price || '0.00'})`}>
            <div className="relative w-40">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[12px] text-secondary-400">£</span>
              <input
                className="input pl-6 text-[12px]"
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={overrides[p]?.price ? (overrides[p].price / 100).toFixed(2) : ''}
                onChange={(e) => setOverride(p, 'price', e.target.value ? String(Math.round(parseFloat(e.target.value) * 100)) : '')}
              />
            </div>
          </Field>
        </div>
      ))}
    </>
  );
}

interface HealthScore {
  score: number; grade: string;
  breakdown: { title: number; description: number; photos: number; pricing: number; tags: number };
  issues: string[]; suggestions: string[];
}

const GRADE_COLOUR: Record<string, string> = {
  A: 'text-secondary-400', B: 'text-primary-300', C: 'text-amber-400', D: 'text-orange-400', F: 'text-danger',
};

function ReviewStep({ form, update }: { form: FormState; update: any }) {
  const [health, setHealth] = useState<HealthScore | null>(null);
  const [checking, setChecking] = useState(false);

  async function checkHealth() {
    setChecking(true);
    try {
      const { data } = await api.post<HealthScore>('/ai/health-score', {
        title: form.title,
        description: form.description,
        pricePence: form.price ? Math.round(parseFloat(form.price) * 100) : undefined,
        photoCount: form.photos.length,
        brand: form.brand,
        category: form.category,
        condition: form.condition || undefined,
        tags: form.tags,
      });
      setHealth(data);
    } catch { /* silently fail if AI not configured */ }
    finally { setChecking(false); }
  }

  const minDate = new Date(Date.now() + 5 * 60 * 1000).toISOString().slice(0, 16);

  return (
    <>
      <header><h2 className="text-[17px] font-semibold">Review & publish</h2><p className="text-xs text-white/30 mt-0.5">Final check before going live</p></header>

      <div className="card p-4 space-y-2 text-sm">
        <Row label="Title" value={form.title || '—'} />
        <Row label="Price" value={form.price ? `£${form.price}` : '—'} />
        {form.buyPrice && <Row label="Buy price" value={`£${form.buyPrice}`} />}
        <Row label="Condition" value={form.condition ? CONDITION_LABELS[form.condition] : '—'} />
        <Row label="Platforms" value={Array.from(form.selectedPlatforms).map((p) => PLATFORM_NAMES[p]).join(', ') || '—'} />
        <Row label="Photos" value={`${form.photos.length} added`} />
        <Row label="Tags" value={form.tags.length > 0 ? form.tags.map((t) => `#${t}`).join(' ') : '—'} />
      </div>

      {/* Listing health score */}
      <div className="card p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-[12.5px] font-semibold">Listing health score</div>
          <button onClick={checkHealth} disabled={checking || !form.title} className="btn-secondary text-[11px] flex items-center gap-1.5">
            {checking ? <><Loader2 size={12} className="animate-spin" /> Checking...</> : <><Sparkles size={12} /> Check quality</>}
          </button>
        </div>
        {health && (
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <div className={`text-3xl font-bold ${GRADE_COLOUR[health.grade] ?? 'text-white/50'}`}>{health.grade}</div>
              <div>
                <div className="text-[13px] font-semibold">{health.score}/100</div>
                <div className="flex gap-1 mt-0.5">
                  {Object.entries(health.breakdown).map(([k, v]) => (
                    <div key={k} className="flex flex-col items-center gap-0.5">
                      <div className="h-8 w-4 bg-white/[0.06] rounded overflow-hidden flex flex-col justify-end">
                        <div className="bg-primary-500/60 rounded" style={{ height: `${v}%` }} />
                      </div>
                      <span className="text-[8px] text-white/20 capitalize">{k.slice(0, 2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {health.issues.length > 0 && (
              <div className="space-y-1">
                {health.issues.map((issue) => (
                  <div key={issue} className="text-[11.5px] text-orange-400 flex items-start gap-1.5">
                    <span className="mt-0.5 flex-shrink-0">⚠</span>{issue}
                  </div>
                ))}
              </div>
            )}
            {health.suggestions.length > 0 && (
              <div className="space-y-1">
                {health.suggestions.map((s) => (
                  <div key={s} className="text-[11.5px] text-secondary-400/80 flex items-start gap-1.5">
                    <span className="mt-0.5 flex-shrink-0">→</span>{s}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Schedule for later */}
      <div className="card p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[12.5px] font-semibold">Schedule for later</div>
            <div className="text-[11px] text-white/30 mt-0.5">Publish automatically at a specific date and time</div>
          </div>
          <button
            onClick={() => update('scheduleEnabled', !form.scheduleEnabled)}
            className={`w-9 h-5 rounded-full relative transition-colors flex-shrink-0 ${form.scheduleEnabled ? 'bg-primary-500' : 'bg-white/10'}`}
          >
            <div className={`w-3.5 h-3.5 rounded-full bg-white absolute top-[3px] transition-all ${form.scheduleEnabled ? 'left-[19px]' : 'left-[3px]'}`} />
          </button>
        </div>
        {form.scheduleEnabled && (
          <input
            type="datetime-local"
            min={minDate}
            className="input text-[12px]"
            value={form.scheduledAt}
            onChange={(e) => update('scheduledAt', e.target.value)}
          />
        )}
        {form.scheduleEnabled && form.scheduledAt && (
          <p className="text-[11px] text-secondary-400">
            Will publish to {form.selectedPlatforms.size} platform{form.selectedPlatforms.size !== 1 ? 's' : ''} on {new Date(form.scheduledAt).toLocaleString('en-GB')}
          </p>
        )}
      </div>
    </>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return <div className="flex justify-between text-xs"><span className="text-white/40">{label}</span><span className="text-white/85 font-medium">{value}</span></div>;
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <div className="text-[11px] font-semibold text-white/30 uppercase tracking-wider">{children}</div>;
}

function Field({ label, children, onAiAction, aiLoading, priceAdvisor, required, error }: {
  label: string;
  children: React.ReactNode;
  onAiAction?: () => void;
  aiLoading?: boolean;
  priceAdvisor?: boolean;
  required?: boolean;
  error?: string;
}) {
  return (
    <div className="flex-1">
      <div className="text-[11px] font-medium text-white/35 mb-1.5 flex items-center justify-between">
        <span>{label}{required && <span className="text-red-400 ml-0.5">*</span>}</span>
        {onAiAction && (
          <button
            onClick={onAiAction}
            disabled={aiLoading}
            className="flex items-center gap-1 text-[10px] text-primary-500 hover:text-primary-400 disabled:opacity-50 transition-colors"
          >
            <Sparkles size={11} className={aiLoading ? 'animate-pulse' : ''} />
            {aiLoading ? 'Generating…' : 'AI generate'}
          </button>
        )}
        {priceAdvisor && (
          <span className="flex items-center gap-1 text-[10px] text-primary-500 cursor-pointer">
            <BarChart3 size={11} /> Price advisor
          </span>
        )}
      </div>
      <div className={error ? 'rounded-lg ring-1 ring-red-500/60' : ''}>{children}</div>
      {error && <p className="text-[10px] text-red-400 mt-1">{error}</p>}
    </div>
  );
}
